import HealthSourceInfo from "@/components/health/HealthSourceInfo";
import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Scale,
  Dumbbell,
  Clock,
  Flame,
  MoreVertical,
  Pencil,
  Trash2,
  TrendingDown,
  TrendingUp,
  Target,
} from "lucide-react";
import { format } from "date-fns";
import ProgressForm from "@/components/progress/ProgressForm";
import MomentumChart from "@/components/progress/MomentumChart";
import SetGoalDialog from "@/components/progress/SetGoalDialog";
import MembershipGate from "@/components/MembershipGate";
import { getUnitPrefs, saveUnitPrefs, KG_TO_LBS, CM_TO_IN } from "@/hooks/useUnitPrefs";
import { useAuth } from "@/lib/AuthContext";

export default function Progress() {
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();
  const { useLbs, useInches } = getUnitPrefs(user);

  // Modal states
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showGoalDialog, setShowGoalDialog] = useState(false);

  // Recent activity tab & expansion
  const [activityTab, setActivityTab] = useState("checkins"); // 'checkins' | 'workouts'
  const [showAllActivity, setShowAllActivity] = useState(false);

  // Unit preferences handlers
  const handleToggleLbs = async (useLbsNew) => {
    await saveUnitPrefs({ useLbs: useLbsNew });
    if (setUser) setUser((prev) => ({ ...prev, use_lbs: useLbsNew }));
  };

  const handleToggleInches = async (useInchesNew) => {
    await saveUnitPrefs({ useInches: useInchesNew });
    if (setUser) setUser((prev) => ({ ...prev, use_inches: useInchesNew }));
  };

  // Queries
  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ["progress"],
    queryFn: () => base44.entities.ProgressLog.list("-date", 200),
  });

  const { data: workoutSessions = [] } = useQuery({
    queryKey: ["workout-sessions"],
    queryFn: () => base44.entities.WorkoutSession.list("-created_date", 50),
  });

  const { data: fitnessGoals = [] } = useQuery({
    queryKey: ["fitness-goals"],
    queryFn: () => base44.entities.FitnessGoal.list(),
  });

  // Active weight goal
  const activeWeightGoal = useMemo(() => {
    return fitnessGoals.find((g) => g.is_active && g.goal_type === "weight") || null;
  }, [fitnessGoals]);

  // Target weight value normalized to current unit preference
  const targetWeightValue = useMemo(() => {
    if (activeWeightGoal?.target_value != null) {
      const val = parseFloat(activeWeightGoal.target_value);
      // If goal was stored with unit kg and user prefers lbs
      if (activeWeightGoal.unit === "kg" && useLbs) return +(val * KG_TO_LBS).toFixed(1);
      if (activeWeightGoal.unit === "lbs" && !useLbs) return +(val / KG_TO_LBS).toFixed(1);
      return +val.toFixed(1);
    }
    if (user?.goal_weight != null && user.goal_weight > 0) {
      const val = parseFloat(user.goal_weight);
      return useLbs ? +val.toFixed(1) : +(val / KG_TO_LBS).toFixed(1);
    }
    return null;
  }, [activeWeightGoal, user?.goal_weight, useLbs]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.ProgressLog.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      queryClient.invalidateQueries({ queryKey: ["fitness-goals"] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.ProgressLog.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      queryClient.invalidateQueries({ queryKey: ["fitness-goals"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.ProgressLog.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      queryClient.invalidateQueries({ queryKey: ["fitness-goals"] });
    },
  });

  // Save Goal Handler
  const handleSaveGoal = async (targetVal) => {
    const unit = useLbs ? "lbs" : "kg";
    if (activeWeightGoal) {
      await base44.entities.FitnessGoal.update(activeWeightGoal.id, {
        target_value: targetVal,
        unit,
      });
    } else {
      await base44.entities.FitnessGoal.create({
        goal_type: "weight",
        target_value: targetVal,
        unit,
        is_active: true,
      });
    }
    if (user) {
      const goalWeightLbs = useLbs ? targetVal : +(targetVal * KG_TO_LBS).toFixed(1);
      await base44.auth.updateMe({ goal_weight: goalWeightLbs });
      if (setUser) setUser((prev) => ({ ...prev, goal_weight: goalWeightLbs }));
    }
    queryClient.invalidateQueries({ queryKey: ["fitness-goals"] });
  };

  // Sort logs chronologically (oldest to newest for calculations)
  const sortedAscLogs = useMemo(() => {
    return [...logs]
      .filter((l) => l && l.date)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [logs]);

  // Sorted newest to oldest for recent activity
  const sortedDescLogs = useMemo(() => {
    return [...logs]
      .filter((l) => l && l.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [logs]);

  // Compute key summary metrics (Main card, Body fat, Waist)
  const summary = useMemo(() => {
    const validWeightLogs = sortedAscLogs.filter((l) => l.weight_kg != null);
    const validBfLogs = sortedAscLogs.filter((l) => l.body_fat_percentage != null);
    const validWaistLogs = sortedAscLogs.filter((l) => l.waist_cm != null);

    const latestWeightLog = validWeightLogs.length > 0 ? validWeightLogs[validWeightLogs.length - 1] : null;
    const earliestWeightLog = validWeightLogs.length > 0 ? validWeightLogs[0] : null;
    const prevWeightLog = validWeightLogs.length > 1 ? validWeightLogs[validWeightLogs.length - 2] : null;

    const latestBfLog = validBfLogs.length > 0 ? validBfLogs[validBfLogs.length - 1] : null;
    const prevBfLog = validBfLogs.length > 1 ? validBfLogs[validBfLogs.length - 2] : null;

    const latestWaistLog = validWaistLogs.length > 0 ? validWaistLogs[validWaistLogs.length - 1] : null;
    const prevWaistLog = validWaistLogs.length > 1 ? validWaistLogs[validWaistLogs.length - 2] : null;

    // Current weight display
    const currentWeight = latestWeightLog
      ? useLbs
        ? +(latestWeightLog.weight_kg * KG_TO_LBS).toFixed(1)
        : +latestWeightLog.weight_kg.toFixed(1)
      : null;

    // Start weight
    const startWeight = earliestWeightLog
      ? useLbs
        ? +(earliestWeightLog.weight_kg * KG_TO_LBS).toFixed(1)
        : +earliestWeightLog.weight_kg.toFixed(1)
      : null;

    // Baseline date for weight change
    let baselineDateStr = "";
    if (earliestWeightLog?.date) {
      try {
        baselineDateStr = format(new Date(earliestWeightLog.date + "T00:00:00"), "MMM d");
      } catch {
        baselineDateStr = earliestWeightLog.date;
      }
    }

    // Weight delta since start
    let weightDiffSinceStart = null;
    if (currentWeight != null && startWeight != null && validWeightLogs.length > 1) {
      weightDiffSinceStart = +(currentWeight - startWeight).toFixed(1);
    }

    // Goal progress calculation
    let goalProgressPct = null;
    if (currentWeight != null && targetWeightValue != null && startWeight != null) {
      if (startWeight > targetWeightValue) {
        // Weight loss
        const total = startWeight - targetWeightValue;
        const progress = startWeight - currentWeight;
        goalProgressPct = total > 0 ? Math.max(0, Math.min(100, Math.round((progress / total) * 100))) : 0;
      } else if (startWeight < targetWeightValue) {
        // Weight gain
        const total = targetWeightValue - startWeight;
        const progress = currentWeight - startWeight;
        goalProgressPct = total > 0 ? Math.max(0, Math.min(100, Math.round((progress / total) * 100))) : 0;
      } else {
        goalProgressPct = currentWeight === targetWeightValue ? 100 : 0;
      }
    }

    // Body fat metrics
    const currentBf = latestBfLog?.body_fat_percentage != null ? +latestBfLog.body_fat_percentage.toFixed(1) : null;
    let bfDiff = null;
    if (currentBf != null && prevBfLog?.body_fat_percentage != null) {
      bfDiff = +(currentBf - prevBfLog.body_fat_percentage).toFixed(1);
    }

    // Waist metrics
    const currentWaist = latestWaistLog
      ? useInches
        ? +(latestWaistLog.waist_cm * CM_TO_IN).toFixed(1)
        : +latestWaistLog.waist_cm.toFixed(1)
      : null;
    let waistDiff = null;
    if (currentWaist != null && prevWaistLog?.waist_cm != null) {
      const prevWaist = useInches
        ? +(prevWaistLog.waist_cm * CM_TO_IN).toFixed(1)
        : +prevWaistLog.waist_cm.toFixed(1);
      waistDiff = +(currentWaist - prevWaist).toFixed(1);
    }

    return {
      currentWeight,
      startWeight,
      baselineDateStr,
      weightDiffSinceStart,
      goalProgressPct,
      currentBf,
      bfDiff,
      currentWaist,
      waistDiff,
      lastWeightKg: latestWeightLog?.weight_kg || null,
    };
  }, [sortedAscLogs, targetWeightValue, useLbs, useInches]);

  // Compute check-in deltas for the activity list
  const checkinRows = useMemo(() => {
    return sortedDescLogs.map((log) => {
      // Find the previous log chronologically (the one directly older than this one)
      const currentIndex = sortedAscLogs.findIndex((l) => l.id === log.id);
      const prevLog = currentIndex > 0 ? sortedAscLogs[currentIndex - 1] : null;

      let weightVal = null;
      let weightDiff = null;

      if (log.weight_kg != null) {
        weightVal = useLbs ? (log.weight_kg * KG_TO_LBS).toFixed(1) : log.weight_kg.toFixed(1);
        if (prevLog?.weight_kg != null) {
          const diff = useLbs
            ? (log.weight_kg * KG_TO_LBS) - (prevLog.weight_kg * KG_TO_LBS)
            : log.weight_kg - prevLog.weight_kg;
          weightDiff = +diff.toFixed(1);
        }
      }

      let dateFormatted = "";
      try {
        dateFormatted = format(new Date(log.date + "T00:00:00"), "MMMM d, yyyy");
      } catch {
        dateFormatted = log.date;
      }

      // Subtitle details
      const details = [];
      if (log.body_fat_percentage != null) {
        details.push(`${log.body_fat_percentage}% body fat`);
      }
      if (log.waist_cm != null) {
        const w = useInches ? (log.waist_cm * CM_TO_IN).toFixed(1) + " in" : log.waist_cm.toFixed(1) + " cm";
        details.push(`${w} waist`);
      }
      if (details.length === 0 && log.notes) {
        details.push(log.notes);
      }

      return {
        ...log,
        dateFormatted,
        weightVal,
        weightDiff,
        detailsSubtitle: details.join(" · ") || "Weight check-in",
      };
    });
  }, [sortedDescLogs, sortedAscLogs, useLbs, useInches]);

  // Items to display in recent activity
  const displayedCheckins = showAllActivity ? checkinRows : checkinRows.slice(0, 3);
  const displayedWorkouts = showAllActivity ? workoutSessions : workoutSessions.slice(0, 3);

  // SVG Circular progress constants
  const radius = 34;
  const strokeWidth = 7;
  const circumference = 2 * Math.PI * radius;
  const progressOffset =
    summary.goalProgressPct != null
      ? circumference - (circumference * summary.goalProgressPct) / 100
      : circumference;

  return (
    <MembershipGate>
      <div className="space-y-5 sm:space-y-6 text-white">
        {/* Top Header: Eyebrow, Title, Unit toggles & Log progress */}
        <div className="flex items-center justify-between pt-1 gap-2">
          <div className="min-w-0">
            <span className="text-[10px] sm:text-[11px] font-bold tracking-widest text-sky-400 uppercase block">
              Your Transformation
            </span>
            <h1 className="text-2xl sm:text-3xl font-display font-black tracking-tight text-white">
              The Progress.
            </h1>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {/* Discreet Unit Switchers */}
            <div className="bg-white/5 border border-white/10 p-0.5 rounded-xl flex items-center gap-0.5 text-xs">
              <button
                type="button"
                onClick={() => handleToggleLbs(true)}
                className={`px-1.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-medium transition-all ${
                  useLbs ? "bg-white/15 text-white shadow-sm" : "text-white/40 hover:text-white"
                }`}
              >
                lbs
              </button>
              <button
                type="button"
                onClick={() => handleToggleLbs(false)}
                className={`px-1.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-medium transition-all ${
                  !useLbs ? "bg-white/15 text-white shadow-sm" : "text-white/40 hover:text-white"
                }`}
              >
                kg
              </button>
              <span className="text-white/20 mx-0.5">|</span>
              <button
                type="button"
                onClick={() => handleToggleInches(true)}
                className={`px-1.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-medium transition-all ${
                  useInches ? "bg-white/15 text-white shadow-sm" : "text-white/40 hover:text-white"
                }`}
              >
                in
              </button>
              <button
                type="button"
                onClick={() => handleToggleInches(false)}
                className={`px-1.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-medium transition-all ${
                  !useInches ? "bg-white/15 text-white shadow-sm" : "text-white/40 hover:text-white"
                }`}
              >
                cm
              </button>
            </div>

            {/* + Log progress Button */}
            <button
              type="button"
              onClick={() => {
                setEditingLog(null);
                setShowLogForm(true);
              }}
              data-testid="progress-log-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/5 hover:bg-sky-500/20 border border-white/10 text-white hover:text-sky-300 font-bold text-xs transition active:scale-95 cursor-pointer shadow-sm"
            >
              <Plus className="w-3.5 h-3.5 text-sky-400 stroke-[2.5]" />
              <span className="hidden sm:inline">Log Progress</span>
              <span className="sm:hidden">Log</span>
            </button>
          </div>
        </div>

        {/* 1. Main Summary Card */}
        <div className="bg-[#131F31] border border-[#233148] rounded-[22px] p-5 sm:p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-white/50">
                CURRENT WEIGHT
              </p>
              <div className="flex items-baseline mt-1">
                <span className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                  {summary.currentWeight != null ? summary.currentWeight : "—"}
                </span>
                <span className="text-base font-semibold text-white/50 ml-1.5">
                  {useLbs ? "lbs" : "kg"}
                </span>
              </div>

              {/* Delta change subtitle */}
              <div className="flex items-center gap-1.5 mt-2 text-sm">
                {summary.weightDiffSinceStart != null ? (
                  <>
                    <span
                      className={`flex items-center font-semibold ${
                        summary.weightDiffSinceStart <= 0 ? "text-[#36D0FF]" : "text-amber-400"
                      }`}
                    >
                      {summary.weightDiffSinceStart <= 0 ? (
                        <TrendingDown className="w-4 h-4 mr-1 inline" />
                      ) : (
                        <TrendingUp className="w-4 h-4 mr-1 inline" />
                      )}
                      {Math.abs(summary.weightDiffSinceStart)} {useLbs ? "lbs" : "kg"}
                    </span>
                    <span className="text-white/50">
                      since {summary.baselineDateStr || "first log"}
                    </span>
                  </>
                ) : summary.currentWeight != null ? (
                  <span className="text-white/50">Baseline check-in recorded</span>
                ) : (
                  <span className="text-white/40">No check-ins yet</span>
                )}
              </div>
            </div>

            {/* Circular Goal-Progress Indicator */}
            <div className="flex flex-col items-center justify-center pl-4">
              {targetWeightValue != null ? (
                <button
                  type="button"
                  onClick={() => setShowGoalDialog(true)}
                  className="group flex flex-col items-center focus:outline-none"
                  title="Click to edit goal"
                >
                  <div className="relative w-20 h-20 flex items-center justify-center">
                    <svg className="w-20 h-20 -rotate-90 transform" viewBox="0 0 80 80">
                      {/* Background track circle */}
                      <circle
                        cx="40"
                        cy="40"
                        r={radius}
                        stroke="#1B2A40"
                        strokeWidth={strokeWidth}
                        fill="transparent"
                      />
                      {/* Active progress circle */}
                      <circle
                        cx="40"
                        cy="40"
                        r={radius}
                        stroke="#36D0FF"
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={progressOffset}
                        strokeLinecap="round"
                        fill="transparent"
                        className="transition-all duration-700 ease-out"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-lg font-extrabold text-white tracking-tight">
                        {summary.goalProgressPct != null ? `${summary.goalProgressPct}%` : "0%"}
                      </span>
                    </div>
                  </div>
                  <span className="text-[11px] font-medium text-white/50 mt-1 group-hover:text-[#36D0FF] transition-colors">
                    of goal
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowGoalDialog(true)}
                  className="flex flex-col items-center justify-center w-20 h-20 rounded-full border-2 border-dashed border-[#233148] hover:border-[#36D0FF] text-white/40 hover:text-[#36D0FF] p-2 transition-all text-center group"
                >
                  <Target className="w-5 h-5 mb-0.5 group-hover:scale-110 transition-transform" />
                  <span className="text-[10px] font-semibold text-white/70">Set goal</span>
                </button>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#233148] my-4" />

          {/* Bottom row: Start & Goal */}
          <div className="flex items-center justify-between text-sm text-white/60">
            <div>
              Start{" "}
              <span className="font-semibold text-white/80">
                {summary.startWeight != null
                  ? `${summary.startWeight} ${useLbs ? "lbs" : "kg"}`
                  : "—"}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <span>Goal</span>
              {targetWeightValue != null ? (
                <button
                  type="button"
                  onClick={() => setShowGoalDialog(true)}
                  className="font-semibold text-white hover:text-[#36D0FF] transition-colors"
                >
                  {targetWeightValue} {useLbs ? "lbs" : "kg"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowGoalDialog(true)}
                  className="text-xs text-[#36D0FF] hover:underline font-semibold"
                >
                  Set goal
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 2. Two Smaller Metric Cards (Body fat & Waist) */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {/* Card: Body fat */}
          <div className="bg-[#131F31] border border-[#233148] rounded-[20px] p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <p className="text-sm font-medium text-white/70">Body fat</p>
              <div className="flex items-baseline mt-1">
                <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  {summary.currentBf != null ? summary.currentBf : "—"}
                </span>
                <span className="text-sm font-semibold text-white/50 ml-1">%</span>
              </div>
            </div>

            <div className="mt-3 text-xs sm:text-sm font-medium">
              {summary.bfDiff != null ? (
                <span
                  className={`flex items-center ${
                    summary.bfDiff <= 0 ? "text-[#36D0FF]" : "text-amber-400"
                  }`}
                >
                  {summary.bfDiff <= 0 ? (
                    <TrendingDown className="w-3.5 h-3.5 mr-1 inline shrink-0" />
                  ) : (
                    <TrendingUp className="w-3.5 h-3.5 mr-1 inline shrink-0" />
                  )}
                  {Math.abs(summary.bfDiff)} percentage points
                </span>
              ) : summary.currentBf != null ? (
                <span className="text-white/40">Baseline recorded</span>
              ) : (
                <span className="text-white/30">Not logged</span>
              )}
            </div>
          </div>

          {/* Card: Waist */}
          <div className="bg-[#131F31] border border-[#233148] rounded-[20px] p-4 sm:p-5 flex flex-col justify-between">
            <div>
              <p className="text-sm font-medium text-white/70">Waist</p>
              <div className="flex items-baseline mt-1">
                <span className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  {summary.currentWaist != null ? summary.currentWaist : "—"}
                </span>
                <span className="text-sm font-semibold text-white/50 ml-1">
                  {useInches ? "in" : "cm"}
                </span>
              </div>
            </div>

            <div className="mt-3 text-xs sm:text-sm font-medium">
              {summary.waistDiff != null ? (
                <span
                  className={`flex items-center ${
                    summary.waistDiff <= 0 ? "text-[#36D0FF]" : "text-amber-400"
                  }`}
                >
                  {summary.waistDiff <= 0 ? (
                    <TrendingDown className="w-3.5 h-3.5 mr-1 inline shrink-0" />
                  ) : (
                    <TrendingUp className="w-3.5 h-3.5 mr-1 inline shrink-0" />
                  )}
                  {Math.abs(summary.waistDiff)} {useInches ? "in" : "cm"} this month
                </span>
              ) : summary.currentWaist != null ? (
                <span className="text-white/40">Baseline recorded</span>
              ) : (
                <span className="text-white/30">Not logged</span>
              )}
            </div>
          </div>
        </div>

        {/* 3. Trends Section: Momentum Chart */}
        <MomentumChart
          logs={logs}
          useLbs={useLbs}
          useInches={useInches}
          weightGoal={targetWeightValue}
        />

        {/* 4. Recent Activity Section */}
        <div className="space-y-4 pt-1">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white tracking-tight">Recent activity</h2>
            {(activityTab === "checkins" ? checkinRows.length > 3 : workoutSessions.length > 3) && (
              <button
                type="button"
                onClick={() => setShowAllActivity((v) => !v)}
                className="text-xs sm:text-sm font-medium text-[#36D0FF] hover:underline"
              >
                {showAllActivity ? "Show less" : "View all →"}
              </button>
            )}
          </div>

          {/* Underlined Sub-tabs: Body check-ins & Workouts */}
          <div className="flex items-center gap-6 border-b border-[#233148] pb-1">
            <button
              type="button"
              onClick={() => setActivityTab("checkins")}
              className={`pb-2.5 text-sm font-semibold transition-colors relative ${
                activityTab === "checkins" ? "text-white" : "text-white/40 hover:text-white/70"
              }`}
            >
              Body check-ins
              {activityTab === "checkins" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#36D0FF] rounded-full" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setActivityTab("workouts")}
              className={`pb-2.5 text-sm font-semibold transition-colors relative ${
                activityTab === "workouts" ? "text-white" : "text-white/40 hover:text-white/70"
              }`}
            >
              Workouts
              {activityTab === "workouts" && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#36D0FF] rounded-full" />
              )}
            </button>
          </div>

          {/* Tab Content: Body check-ins */}
          {activityTab === "checkins" && (
            <div className="space-y-2.5">
              {displayedCheckins.length === 0 ? (
                <div className="bg-[#131F31] border border-[#233148] rounded-[20px] p-8 text-center text-white/50">
                  <Scale className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="font-semibold text-white/80 text-sm">No body check-ins yet</p>
                  <p className="text-xs text-white/40 mt-1">
                    Tap “+ Log progress” above to record your first weigh-in.
                  </p>
                </div>
              ) : (
                displayedCheckins.map((row) => (
                  <div
                    key={row.id}
                    className="bg-[#131F31] border border-[#233148] hover:border-[#36D0FF]/40 rounded-[18px] p-3.5 sm:p-4 flex items-center justify-between gap-3 transition-colors"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      {/* Cyan Scale Icon */}
                      <div className="w-11 h-11 rounded-xl bg-[#132A3D] border border-[#36D0FF]/25 text-[#36D0FF] flex items-center justify-center shrink-0">
                        <Scale className="w-5 h-5" />
                      </div>

                      <div className="min-w-0">
                        <p className="font-bold text-white text-sm truncate">{row.dateFormatted}</p>
                        <p className="text-xs text-white/50 mt-0.5 truncate">{row.detailsSubtitle}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      {/* Weight and delta */}
                      <div className="text-right">
                        {row.weightVal ? (
                          <>
                            <p className="font-bold text-white text-sm">
                              {row.weightVal} {useLbs ? "lbs" : "kg"}
                            </p>
                            {row.weightDiff != null ? (
                              <p
                                className={`text-xs font-semibold ${
                                  row.weightDiff <= 0 ? "text-[#36D0FF]" : "text-amber-400"
                                }`}
                              >
                                {row.weightDiff <= 0 ? "↓" : "↑"} {Math.abs(row.weightDiff)}{" "}
                                {useLbs ? "lbs" : "kg"}
                              </p>
                            ) : (
                              <p className="text-xs text-white/30">—</p>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-white/40">Measurements only</span>
                        )}
                      </div>

                      {/* Row Action Menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/5 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-[#0C1424] border border-[#233148] text-white rounded-xl shadow-xl min-w-[130px]"
                        >
                          <DropdownMenuItem
                            onClick={() => {
                              setEditingLog(row);
                              setShowLogForm(true);
                            }}
                            className="cursor-pointer text-xs text-white/80 hover:text-white hover:bg-white/5 gap-2"
                          >
                            <Pencil className="w-3.5 h-3.5 text-[#36D0FF]" /> Edit check-in
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeletingId(row.id)}
                            className="cursor-pointer text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 gap-2"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tab Content: Workouts */}
          {activityTab === "workouts" && (
            <div className="space-y-2.5">
              {displayedWorkouts.length === 0 ? (
                <div className="bg-[#131F31] border border-[#233148] rounded-[20px] p-8 text-center text-white/50">
                  <Dumbbell className="w-10 h-10 text-white/20 mx-auto mb-3" />
                  <p className="font-semibold text-white/80 text-sm">No workouts logged yet</p>
                  <p className="text-xs text-white/40 mt-1">
                    Start a workout from the Hub to record your training activity.
                  </p>
                </div>
              ) : (
                displayedWorkouts.map((ws) => (
                  <div
                    key={ws.id}
                    className="bg-[#131F31] border border-[#233148] rounded-[18px] p-3.5 sm:p-4 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-[#132A3D] border border-[#36D0FF]/25 text-[#36D0FF] flex items-center justify-center shrink-0">
                        <Dumbbell className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-white text-sm truncate">
                          {ws.plan_name || "Workout Session"}
                        </p>
                        <p className="text-xs text-white/50 mt-0.5">
                          {ws.completed_date
                            ? format(new Date(ws.completed_date + "T00:00:00"), "MMM d, yyyy")
                            : ws.started_at
                            ? format(new Date(ws.started_at), "MMM d, yyyy")
                            : "Recent"}
                        </p>
                      </div>
                    </div>

                    <div className="text-right text-xs text-white/60 shrink-0 space-y-0.5">
                      <div className="flex items-center justify-end gap-1 font-semibold text-white">
                        <Clock className="w-3 h-3 text-[#36D0FF]" />
                        {formatDuration(ws.duration_seconds)}
                      </div>
                      {ws.calories_burned ? (
                        <div className="flex items-center justify-end gap-1 text-white/50">
                          <Flame className="w-3 h-3 text-amber-400" />
                          {ws.calories_burned} kcal<HealthSourceInfo topic="exercise" />
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Edit / Create Progress Log Modal */}
        <ProgressForm
          open={showLogForm}
          onOpenChange={(v) => {
            setShowLogForm(v);
            if (!v) setEditingLog(null);
          }}
          initialData={editingLog}
          useLbs={useLbs}
          useInches={useInches}
          lastWeightKg={summary.lastWeightKg}
          onSubmit={(cleaned) => {
            if (editingLog) {
              updateMutation.mutate({ id: editingLog.id, data: cleaned });
            } else {
              createMutation.mutate(cleaned);
            }
          }}
        />

        {/* Set Goal Modal */}
        <SetGoalDialog
          open={showGoalDialog}
          onOpenChange={setShowGoalDialog}
          currentGoalWeight={targetWeightValue}
          useLbs={useLbs}
          onSave={handleSaveGoal}
        />

        {/* Delete Confirmation Alert */}
        <AlertDialog open={Boolean(deletingId)} onOpenChange={(v) => !v && setDeletingId(null)}>
          <AlertDialogContent className="bg-[#131F31] border border-[#233148] text-white rounded-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lg font-bold text-white font-display">
                Delete check-in?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-white/60 text-sm">
                This will permanently delete this progress record. Your charts and trends will
                automatically update.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex gap-2">
              <AlertDialogCancel className="bg-transparent border-[#233148] text-white hover:bg-white/5">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (deletingId) {
                    deleteMutation.mutate(deletingId);
                    setDeletingId(null);
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white font-semibold"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </MembershipGate>
  );
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  if (total < 60) return `${total}s`;
  const mm = Math.floor(total / 60);
  if (mm < 60) return `${mm}m`;
  const hh = Math.floor(mm / 60);
  const rem = mm % 60;
  return rem === 0 ? `${hh}h` : `${hh}h ${rem}m`;
}